import { useState } from 'react';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  Heading,
  VStack,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Divider,
  Box,
  Button,
  Text,
  LightbulbIcon,
  OutlineCircleQuestionIcon,
  DrawerCloseButton,
} from '@frontend/components';

import { SupportDrawer } from 'components/support';

import { RELEASE, SERVER } from 'constants/all';

import { capitalize } from 'utils/stringUtils';

import HelpModeResourceCard from './HelpModeResourceCard';
import PageSpecificContentDrawer from './PageSpecificContentDrawer';
import SelectVideoButton from './SelectVideoButton';

const LoadingAlert = () => (
  <Alert status="info" mt={2}>
    <AlertIcon />
    <AlertTitle>Loading the most relevant videos for you</AlertTitle>
  </Alert>
);

const trimSemVerFixVersion = (version) => {
  return version.split('.').slice(0, -1).join('.');
};

const HelpModeDrawer = ({
  isLoading,
  isOpen,
  onClose,
  videoCategories,
  onVideoSelect,
  isPageSpecificDrawerOpen,
  onPageSpecificDrawerClose,
  pageId,
}) => {
  const [isSupportDrawerOpen, setIsSupportDrawerOpen] = useState(false);

  // show only the major and minor versions of the release
  const displayableAppVersion = RELEASE
    ? trimSemVerFixVersion(RELEASE)
    : `(${capitalize(SERVER)})`;

  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="md">
      <DrawerContent>
        <DrawerHeader>
          Help
          <DrawerCloseButton />
        </DrawerHeader>
        <DrawerBody pb={0} pt={0}>
          {isOpen && (
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              minHeight="100%"
            >
              {/* Let the user know if the list is not completely loaded */}
              {isLoading ? <LoadingAlert /> : null}

              {/* If loading completes and there are videos, show them */}
              {videoCategories.length
                ? videoCategories.map((category) => (
                    <>
                      <VStack
                        key={category.title}
                        align="start"
                        spacing={2}
                        w="full"
                        mb={2}
                      >
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          width="100%"
                          pt={2}
                        >
                          <Heading size="lg" weight="medium">
                            {category.title}
                          </Heading>
                          {category.expandLinkText &&
                            (typeof category.onExpandLinkClick ===
                            'function' ? (
                              <Button
                                variant="link"
                                onClick={category.onExpandLinkClick}
                                size="md"
                              >
                                {category.expandLinkText}
                              </Button>
                            ) : (
                              <Button
                                as="a"
                                href={category.onExpandLinkClick}
                                variant="link"
                                target="_blank"
                                size="md"
                              >
                                {category.expandLinkText}
                              </Button>
                            ))}
                        </Box>
                        {category.videos.length > 0 &&
                          ((video) => (
                            <SelectVideoButton
                              key={video.id}
                              video={video}
                              onClick={() =>
                                onVideoSelect(video, {
                                  isTopLevelDrawerOpen: isOpen,
                                  isPageSpecificDrawerOpen,
                                })
                              }
                            />
                          ))(category.videos[0])}
                      </VStack>
                      <Divider />
                    </>
                  ))
                : null}

              <VStack spacing={2} width="100%">
                <Box width="100%" pt={2}>
                  <Heading size="lg" weight="medium" mr="auto">
                    Resources
                  </Heading>
                </Box>
                <HelpModeResourceCard
                  title="Knowledge Base"
                  icon={<LightbulbIcon />}
                  description="Browse tutorials, articles, videos, and more"
                  as="a"
                  target="_blank"
                  href="https://help.paperlessparts.com"
                />
                <HelpModeResourceCard
                  title="Contact Support"
                  icon={<OutlineCircleQuestionIcon />}
                  description="Ask a question, report a bug, or submit feedback"
                  onClick={() => {
                    setIsSupportDrawerOpen(!isSupportDrawerOpen);
                  }}
                />
              </VStack>
              {displayableAppVersion && (
                <Text
                  ml="auto"
                  mt="auto"
                  color="feature.secondary"
                  py={2}
                  textAlign="end"
                  size="sm"
                >
                  Paperless Parts {displayableAppVersion}
                </Text>
              )}
            </Box>
          )}
        </DrawerBody>
        <PageSpecificContentDrawer
          pageId={pageId}
          isOpen={isPageSpecificDrawerOpen}
          onClose={onPageSpecificDrawerClose}
          onVideoSelect={onVideoSelect}
        />
        <SupportDrawer
          isOpen={isSupportDrawerOpen}
          onClose={() => {
            setIsSupportDrawerOpen(false);
            onClose();
          }}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default HelpModeDrawer;
